import { Link, useNavigate } from 'react-router-dom';
import { MoreVertical, LogOut } from 'lucide-react';
import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { SeasonBadge } from './SeasonBadge';
import { setActivePropertyId } from '@/lib/auth';
import { logout } from '@/lib/api';
import { NAV } from './Sidebar';
import { cn } from '@/lib/utils';
import AccountSwitcher from './AccountSwitcher';
import PropertySwitcher from './PropertySwitcher';

/**
 * App-wide top header. Two compositions in one component:
 *
 *   Mobile (`< md`): brand + season + overflow menu (`⋯`). The
 *     overflow menu opens a right-side Sheet that mirrors the desktop
 *     sidebar — every long-tail route remains reachable in two taps.
 *
 *   Desktop (`md` and up): brand + season + sign-out button inline.
 *     Sidebar handles navigation; no overflow menu needed.
 */
export default function Header() {
  return (
    <header className="sticky top-0 z-30 border-b border-rule bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container flex h-14 items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" aria-label="HouseOps" className="flex items-baseline gap-2 shrink-0">
            <span className="font-display text-xl font-semibold tracking-tight text-foreground">
              HouseOps
            </span>
            <span
              aria-hidden
              className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              Field log
            </span>
          </Link>
          <AccountSwitcher />
        </div>
        <div className="flex items-center gap-2">
          <PropertySwitcher />
          <SeasonBadge />
          <SignOutButton />
          <OverflowMenu />
        </div>
      </div>
    </header>
  );
}

function SignOutButton() {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={async () => {
        try {
          await logout();
        } catch {
          /* ignore */
        }
        setActivePropertyId(null);
        window.location.assign('/auth/sign-in');
      }}
      className="hidden md:inline-flex"
    >
      Sign out
    </Button>
  );
}

function OverflowMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="md:hidden">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label="More navigation"
      >
        <MoreVertical className="h-5 w-5" />
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Navigate</SheetTitle>
          </SheetHeader>
          <ul className="mt-4 space-y-1 text-sm">
            {NAV.map((item) => (
              <li key={item.to}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(item.to);
                    setOpen(false);
                  }}
                  className={cn(
                    'block w-full rounded-md px-3 py-2 text-left text-foreground/85 transition-colors',
                    'hover:bg-muted hover:text-foreground',
                  )}
                >
                  {item.label}
                </button>
              </li>
            ))}
            <li className="pt-2 mt-2 border-t border-rule">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await logout();
                  } catch {
                    /* ignore */
                  }
                  setActivePropertyId(null);
                  window.location.assign('/auth/sign-in');
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-foreground/85 hover:bg-muted hover:text-foreground"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Sign out
              </button>
            </li>
          </ul>
        </SheetContent>
      </Sheet>
    </div>
  );
}
